import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Popper,
  Stack,
  Typography,
} from "@mui/material";

// project imports
import MainCard from "ui-component/cards/MainCard";
import Transitions from "ui-component/extended/Transitions";

// assets
import { IconLogout, IconEye, IconEyeOff } from "@tabler/icons";
import { color } from "utils/color";
import API from "api/axios";
import { toast } from "react-toastify";
import ApiRoutes from "api/endpoint";

const ProfileSection = ({ name, email, type }) => {
  const theme = useTheme();
  const customization = useSelector((state) => state.customization);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  // Change Password Modal State
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Retrieve logged in user and compute role
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem("user") || "{}");
  } catch (e) {
    user = {};
  }

  const formatRoleName = (str) => {
    if (!str) return "";
    return str
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };

  let displayRole = "Admin";
  if (user?.role === "super_admin") {
    displayRole = "Admin";
  } else if (user?.roles && user.roles.length > 0) {
    const roleNames = user.roles
      .map((r) => (typeof r === "object" ? r.name : r))
      .filter(Boolean)
      .map(formatRoleName);
    if (roleNames.length > 0) {
      displayRole = roleNames.join(", ");
    }
  } else if (user?.role && user.role !== "user") {
    displayRole = formatRoleName(user.role);
  }

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const userEmail = user?.email || email || "";
  const avatarLetter = (user?.firstName?.[0] || displayRole?.[0] || "A").toUpperCase();

  const handleLogout = async () => {
    try {
      await API.request({
        url: ApiRoutes.Auth.Logout.Endpoint,
        method: ApiRoutes.Auth.Logout.Method,
      });
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      localStorage.clear();
      navigate("/");
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current && !open) anchorRef.current?.focus();
    prevOpen.current = open;
  }, [open]);

  // Change Password Handlers
  const handleOpenChangePassword = () => {
    setOpen(false);
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setChangePasswordOpen(true);
  };

  const handleCloseChangePassword = () => {
    if (changingPassword) return;
    setChangePasswordOpen(false);
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword.trim()) {
      return toast.warn("Please enter a new password.");
    }
    if (newPassword.trim().length < 6) {
      return toast.warn("Password must be at least 6 characters.");
    }
    if (newPassword !== confirmPassword) {
      return toast.warn("Passwords do not match.");
    }

    try {
      setChangingPassword(true);
      const res = await API.patch(ApiRoutes.User.ChangePasswordDirect.Endpoint, {
        newPassword: newPassword.trim(),
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Password updated successfully!");
        handleCloseChangePassword();
      } else {
        toast.error(res.data?.message || "Failed to update password");
      }
    } catch (err) {
      console.error("Change password error:", err);
      toast.error(err.response?.data?.message || "Failed to update password");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <>
      <Avatar
        ref={anchorRef}
        onClick={handleToggle}
        sx={{
          ...theme.typography.mediumAvatar,
          cursor: "pointer",
          backgroundColor: "#1565c0",
          color: "#fff",
          fontWeight: 700,
          fontSize: "14px",
          transition: "all .2s ease-in-out",
          "&:hover": {
            backgroundColor: "#0d47a1",
            color: "#fff",
          },
        }}
      >
        {avatarLetter}
      </Avatar>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        modifiers={[{ name: "offset", options: { offset: [0, 14] } }]}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper
              sx={{
                width: 250,
                borderRadius: "14px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.08)",
                overflow: "hidden",
                backgroundColor: "#FFF",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} content={false}>
                  {/* User Greeting Header */}
                  <Box sx={{ p: 2, pb: 1.5 }}>
                    <Stack spacing={0.3}>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: "#0F172A", fontSize: "15px" }}>
                        Welcome Back, {displayRole}
                      </Typography>
                      {fullName && (
                        <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500, fontSize: "12px" }}>
                          {fullName}
                        </Typography>
                      )}
                      {userEmail && (
                        <Typography variant="caption" sx={{ color: "#94A3B8", fontSize: "11px" }}>
                          {userEmail}
                        </Typography>
                      )}
                    </Stack>
                  </Box>

                  {/* Actions Section */}
                  <Box sx={{ px: 2, pb: 2, display: "flex", flexDirection: "column", gap: 1.2 }}>
                    {/* Change Password Button */}
                    <Button
                      fullWidth
                      onClick={handleOpenChangePassword}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "#E2E8F0",
                        color: "#1E293B",
                        border: "1px solid #94A3B8",
                        py: 1,
                        fontWeight: 600,
                        fontSize: "13px",
                        textTransform: "none",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: "#CBD5E1",
                          borderColor: "#64748B",
                        },
                      }}
                    >
                      Change Password
                    </Button>

                    {/* Logout Button */}
                    <Button
                      fullWidth
                      onClick={handleLogout}
                      sx={{
                        borderRadius: "8px",
                        backgroundColor: "#DC2626",
                        color: "#ffffff",
                        py: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "13px",
                        boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: "#B91C1C",
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 10px rgba(220, 38, 38, 0.35)",
                        },
                      }}
                    >
                      <IconLogout size="1.2rem" stroke={2} />
                      Logout
                    </Button>
                  </Box>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      {/* CHANGE PASSWORD DIALOG MODAL */}
      <Dialog
        open={changePasswordOpen}
        onClose={handleCloseChangePassword}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1.5,
            boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle sx={{ px: 2, pt: 1.5, pb: 2, fontWeight: 700, fontSize: "18px", color: "#0F172A" }}>
          Change Account Password
        </DialogTitle>

        <form onSubmit={handleChangePasswordSubmit}>
          <DialogContent sx={{ px: 2, py: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* New Password */}
            <Box>
              <div style={{ position: "relative" }}>
                <input
                  type={showNewPassword ? "text" : "password"}
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New Password *"
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "12px 42px 12px 14px",
                    borderRadius: "8px",
                    border: "1px solid #CBD5E1",
                    fontSize: "14px",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#10B981")}
                  onBlur={(e) => (e.target.style.borderColor = "#CBD5E1")}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#64748B",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                  }}
                  title={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                </button>
              </div>
            </Box>

            {/* Confirm New Password */}
            <Box>
              <div style={{ position: "relative" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm New Password *"
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "12px 42px 12px 14px",
                    borderRadius: "8px",
                    border: "1px solid #CBD5E1",
                    fontSize: "14px",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#10B981")}
                  onBlur={(e) => (e.target.style.borderColor = "#CBD5E1")}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#64748B",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                  }}
                  title={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                </button>
              </div>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 2, pt: 3, pb: 1, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <button
              type="button"
              onClick={handleCloseChangePassword}
              disabled={changingPassword}
              style={{
                background: "none",
                border: "none",
                color: "#475569",
                fontWeight: "600",
                fontSize: "14px",
                cursor: "pointer",
                padding: "8px 16px",
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={changingPassword}
              style={{
                background: "#00a86b",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "8px",
                padding: "10px 22px",
                fontWeight: "600",
                fontSize: "14px",
                cursor: changingPassword ? "not-allowed" : "pointer",
                opacity: changingPassword ? 0.7 : 1,
                boxShadow: "0 2px 6px rgba(0, 168, 107, 0.3)",
                transition: "background-color 0.2s",
              }}
            >
              {changingPassword ? "Updating..." : "Change Password"}
            </button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default ProfileSection;